import React from "react";
import { Link } from "react-router-dom";
import { pricingPlans } from "../mock";
import { Button } from "./ui/button";
import { Check, ArrowRight } from "lucide-react";
import Reveal from "./Reveal";

export default function Pricing() {
  return (
    <section id="pricing" className="relative py-24 md:py-32 bg-[#f7f3ea]">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <div className="text-center mb-16">
            <p className="font-mono-eyebrow text-[#486a8f] mb-4">Pricing</p>
            <h2 className="font-display text-[36px] sm:text-[40px] md:text-[56px] leading-[1.05] text-[#0f1724] tracking-tight">
              Keep your roadmap{" "}
              <span className="italic font-light text-[#486a8f]">alive</span>
            </h2>
            <p className="font-body text-[14px] md:text-[16px] text-[#4b5563] mt-4 max-w-xl mx-auto leading-relaxed px-2">
              Your coach remembers your goals, checks in daily, and keeps adjusting your plan as your life changes.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {pricingPlans.map((plan, i) => (
            <Reveal key={plan.name} delay={i * 0.12}>
              <div
                className={`relative rounded-3xl p-10 md:p-12 border card-hover ${
                  plan.ctaVariant === "green"
                    ? "bg-white border-[#3e6b52]/40 shadow-[0_20px_60px_-30px_rgba(62,107,82,0.35)]"
                    : "bg-white border-[#eadfc9]"
                }`}
              >
                <span
                  className={`absolute -top-3 left-10 px-4 py-1.5 rounded-full font-mono-eyebrow text-[10px] ${
                    plan.badgeVariant === "green"
                      ? "bg-[#3e6b52] text-white"
                      : "bg-[#486a8f] text-white"
                  }`}
                >
                  {plan.badge}
                </span>
                <p className="font-mono-eyebrow text-[#6b7280] mb-4">{plan.name}</p>
                <div className="flex items-end gap-1 mb-2">
                  <span className="font-display text-[64px] md:text-[78px] leading-none text-[#0f1724] tracking-tight">
                    {plan.price}
                  </span>
                  <span className="font-body text-[#6b7280] mb-3 text-[15px]">{plan.period}</span>
                </div>
                {plan.annualPrice && (
                  <p className="font-body text-[12px] text-[#6b7280] mb-7">
                    or <span className="text-[#0f1724] font-medium">{plan.annualPrice}</span> — {plan.annualNote}
                  </p>
                )}
                <ul className="space-y-3.5 mb-10">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                          plan.ctaVariant === "green"
                            ? "bg-[#e5efe8] text-[#3e6b52]"
                            : "bg-[#e8eef6] text-[#486a8f]"
                        }`}
                      >
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span className="font-body text-[15px] text-[#2b3442]">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/pricing">
                  <Button
                    className={`group w-full h-13 py-3 rounded-full font-medium text-[15px] btn-lift ${
                      plan.ctaVariant === "green"
                        ? "bg-[#3e6b52] hover:bg-[#335944] text-white"
                        : "bg-[#486a8f] hover:bg-[#3a5a7c] text-white"
                    }`}
                  >
                    {plan.ctaLabel}
                    <ArrowRight
                      size={16}
                      className="ml-2 transition-transform group-hover:translate-x-1"
                    />
                  </Button>
                </Link>
                <p className="font-body text-[12px] text-[#6b7280] text-center mt-4">{plan.footnote}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="text-center font-body text-[13px] text-[#6b7280] mt-10">
            Both plans include a 3-day free trial — cancel anytime.
          </p>
          <p className="text-center font-body text-[12px] text-[#9aa3ae] mt-4 max-w-2xl mx-auto leading-relaxed px-4">
            Ascend Higher is for personal growth, organization, reflection, and habit-building. It is not therapy,
            medical care, financial advice, legal advice, or crisis support.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
